// The Studio's offer card — plans and checkout INSIDE the modal.
//
// Why this exists as its own component: the other plan ceilings still render
// the shared `UpgradeModal` card, whose CTA is a <Link> to /pricing. Only the
// reasons routed here get the offer, so it can be added (or removed) one
// ceiling at a time. See UpgradeModal.tsx — its only change is an early return.
//
// The argument the design makes: show the user the picture of the limit they
// just hit — the free minute lit and the rest of the track locked, or the
// separations they have spent — before any copy. Then the price, then one
// button that says what it charges.
//
// Everything below is our own vocabulary: --sx-* tokens from studio-next.css,
// the gradient/gold pair from pricing.css, Plus Jakarta Sans. Nothing describes
// how separation works — naming an engine in user-facing copy is a blocker.
//
// ── VARIANTS (2026-09-03) ───────────────────────────────────────────────────
// 'length' — the 60s section cap. The original card, unchanged.
// 'quota'  — out of separations for the 24h window. Same card, its own head.
// 'export' — the export ceiling: 'trimmed' (only the first N seconds come out)
//            or 'wav' (a Pro-only format, no way through).
// 'quality' — Best quality picked on a free plan (2026-09-19). The wall with the
//            best click-through in the Studio (8 of 56 in the prior 14 days)
//            and, until now, a price-less link.
// 'chords'  — the chord ceiling (free reads the first FREE_CHORDS_SECONDS).
//            Sold a $118 Lifetime through the extension's version of it.
// Adding a variant means adding a HEAD block below; the plans, cycle toggle,
// checkout and footer are shared and know nothing about which one is showing.
//
// ⚠️ Neither new variant adds an interruption. Both dialogs already opened at
// exactly these moments and ended in a price-less link to /pricing; only the
// offer inside them changed. The export card keeps its way through as the
// secondary action, so nothing that used to complete now stops.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { billing } from '../lib/api';
import { track } from '../lib/track';
import { funnelEvent } from '../lib/funnel';
import './fullSongUpsell.css';

type Cycle = 'monthly' | 'yearly';
type PlanKey = 'pro' | 'lifetime';

/** Which ceiling opened the card. Rides on both analytics events as the
 *  context, so /admin can tell which wall actually converts. */
export type UpsellVariant = 'length' | 'quota' | 'export' | 'quality' | 'chords';

interface Props {
  onClose: () => void;
  /** Signed-out visitors sign in first — a purchase must attach to an account. */
  anonymous?: boolean;
  /** Defaults to the original 60s-cap card. */
  variant?: UpsellVariant;
  /** 'length': free section ceiling in seconds, so the copy can never drift. */
  freeSeconds?: number;
  /** 'quota': free separations per rolling 24h window. */
  dailyQuota?: number;
  /** 'export': which export ceiling — 'wav' has no way through, 'trimmed' does. */
  exportKind?: 'wav' | 'trimmed';
  /** 'export': how many seconds a free export covers. */
  exportSeconds?: number;
  /** 'chords': free chord ceiling in seconds (FREE_CHORDS_SECONDS), so the
   *  copy and the ribbon sketch can never drift from the real gate. */
  freeChordsSeconds?: number;
  /** Replaces "Keep using free" — the export card's way through, so the
   *  action the user asked for can still complete from inside the offer. */
  secondary?: { label: string; onClick: () => void } | null;
  /** Fired the moment the user commits to buying, before checkout opens, so a
   *  surface with its own funnel (the export gate) keeps recording intent. */
  onUpgradeIntent?: () => void;
  /** 'length': where the free section actually sits in the song, when it is
   *  not the first N seconds — the auto-split page (/upload-test) separates the
   *  BUSIEST minute, and the picture must light that stretch, not 0:00.
   *  Absent = the original card, unchanged. */
  freeWindow?: { start: number; end: number; total: number; busiest?: boolean };
  /** What opened the card ('edge' | 'header' | 'badge'), riding on both events.
   *  Absent = both events carry exactly what they always have. */
  trigger?: string;
  /** Which flow opened it ('auto' = the auto-split page). Same rule as trigger. */
  flow?: string;
}

/**
 * A static bar sketch of a track: the first `BARS_FREE` bars are the part the
 * free plan covers, the rest are the part that stays locked. Heights are fixed
 * (not random) so the modal looks identical every time it opens — a waveform
 * that reshuffles on each open reads as decoration, not as your song.
 */
const BAR_HEIGHTS = [
  18, 26, 41, 33, 47, 29, 38, 52, 35, 44, 24, 39, 49, 31, 42, 27, 36, 46,
  30, 40, 51, 34, 25, 43, 37, 48, 28, 45, 32, 50, 23, 41, 36, 47, 29, 39,
  44, 26, 38, 49, 31, 42, 34, 27, 45, 22,
];
const BARS_FREE = 12;

// The chord ceiling's sketch: a plain progression, fixed so the ribbon looks
// identical every time it opens. The first CHORDS_FREE bars are the ones free
// reads; the rest are still on the track, just not named yet.
const CHORD_RIBBON = ['C', 'G', 'Am', 'F', 'C', 'G', 'Am', 'F', 'Dm', 'Em', 'F', 'G'];
const CHORDS_FREE = 4;

function LockIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2.2" />
      <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
    </svg>
  );
}

function SparkIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l2.2 5.4L20 10.5l-5.8 2.1L12 18l-2.2-5.4L4 10.5l5.8-2.1z" />
      <path d="M18.6 16.6l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  );
}

function ClockIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.4v5l3.3 1.9" />
    </svg>
  );
}

// Benefit glyphs. Each variant picks three; they are drawn at 17px, so the
// shapes stay simple enough to read at that size.
const B_SONG = <path d="M9 18V5l12-2v13" />;
const B_SONG2 = <><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>;
const B_CHORDS = <><path d="M5 6h14" /><path d="M5 6v13M9.7 6v13M14.3 6v13M19 6v13" /><path d="M5 10.3h14M5 14.6h14" /></>;
const B_DOWNLOAD = <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></>;
const B_INFINITY = <path d="M6.6 15.4a3.4 3.4 0 1 1 0-6.8c2.4 0 3.4 3.4 5.4 3.4s3-3.4 5.4-3.4a3.4 3.4 0 1 1 0 6.8c-2.4 0-3-3.4-5.4-3.4s-3 3.4-5.4 3.4z" />
const B_SPARK = <><path d="M12 3l2.2 5.4L20 10.5l-5.8 2.1L12 18l-2.2-5.4L4 10.5l5.8-2.1z" /><path d="M18.6 16.6l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" /></>;
const B_LAYERS = <><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" /></>;
const B_WAVE = <><path d="M3 12h2.5l2-6 3 13 3-9 2 4H21" /></>;
const B_METRONOME = <><path d="M9.6 3h4.8l4.1 18H5.5z" /><path d="M7.2 15h9.6" /><path d="M15.8 6.6L10.6 17" /></>;
const B_SYNC = <><path d="M4 7h11a4 4 0 010 8H9" /><path d="M9 15l3 3m-3-3l3-3" /></>;

function BenefitIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

// Reads after "the first …": "minute" / "90 seconds" / "2 minutes". Derived
// from the real cap so the copy can't rot the way a hardcoded number does —
// and worded so 60 gives "the first minute", not "the first 1 minute".
function capAfterFirst(sec: number): string {
  if (sec % 60 !== 0) return `${sec} seconds`;
  const m = sec / 60;
  return m === 1 ? 'minute' : `${m} minutes`;
}

// The cap as a timeline stamp for the waveform legend: 60 → "1:00".
function capStamp(sec: number): string {
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

export function FullSongUpsell({
  onClose,
  anonymous = false,
  variant = 'length',
  freeSeconds = 60,
  dailyQuota = 5,
  exportKind = 'trimmed',
  exportSeconds = 60,
  freeChordsSeconds = 30,
  secondary = null,
  onUpgradeIntent,
  freeWindow,
  trigger,
  flow,
}: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Trust our own auth state as well as the caller's flag: the length ceiling
  // fires from a drag handler that doesn't refresh `anonymous`, so a stale
  // `true` would otherwise show "Sign in" to someone who is signed in.
  const isAnon = anonymous || !user;

  // Founder's Lifetime is PRE-SELECTED (owner, 2026-09-03, on every variant).
  // Lifetime was 89% of the last 30 days' revenue from a third of the buyers,
  // and the first price a reader sees is the anchor: $99 first makes $5.95 read
  // as trivial, so the one-song buyer still takes Monthly — one click away, and
  // still badged "Most popular" because it is. Reverses the 2026-08-21 call
  // (Monthly first) that predates any lifetime data. Revert = 'pro' here.
  const [plan, setPlan] = useState<PlanKey>('lifetime');
  // Monthly stays the cycle Pro opens on when picked — the lower entry price
  // leads, with the yearly saving shown beside the toggle.
  const [cycle, setCycle] = useState<Cycle>('monthly');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isWav = variant === 'export' && exportKind === 'wav';
  // What rides on both events. 'length' keeps its original payload exactly, so
  // the numbers already collected stay comparable.
  const evt = useMemo(
    () => ({
      ...(variant === 'export' ? { reason: 'export', kind: exportKind } : { reason: variant }),
      // Only when given, so every existing caller's payload is byte-for-byte as before.
      ...(trigger ? { trigger } : {}),
      ...(flow ? { flow } : {}),
    }),
    [variant, exportKind, trigger, flow],
  );

  useEffect(() => {
    track('rl_upgrade_modal_open', { ...evt, surface: 'studio', tier: isAnon ? 'anonymous' : 'free' });
    // Mirrored into our own DB so this wall is readable from /admin — GA4 is
    // denied in the EEA/UK and dropped by blockers, and the name is already on
    // the server's accepted list, so this is additive.
    funnelEvent('rl_upgrade_modal_open', { ...evt, tier: isAnon ? 'anonymous' : 'free' }, 'studio');
  }, [isAnon, evt]);

  // Escape closes, matching every other dialog in the Studio.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isLife = plan === 'lifetime';
  const yearly = cycle === 'yearly';

  const bars = useMemo(
    () => BAR_HEIGHTS.map((h, i) => ({ h, free: i < BARS_FREE })),
    [],
  );

  const ctaLabel = isLife
    ? 'Get Lifetime — $99 once'
    : yearly ? 'Get Pro — $39/year' : 'Get Pro — $5.95/month';

  async function onBuy() {
    const planKey = isLife ? 'lifetime' : yearly ? 'pro_yearly' : 'pro_monthly';
    const tagged = { ...(trigger ? { trigger } : {}), ...(flow ? { flow } : {}) };
    track('rl_upgrade_click', { source: 'upgrade_modal', plan: planKey, context: variant, surface: 'studio', ...tagged });
    funnelEvent('rl_upgrade_click', { source: 'upgrade_modal', plan: planKey, context: variant, ...tagged }, 'studio');
    // Lets a surface with its own funnel record the same intent without
    // closing this card — checkout is about to take over the tab.
    onUpgradeIntent?.();

    // Guests sign in first, so the purchase ties to a real account (Riffloop
    // attributes by email). Same destination the old modal used.
    if (isAnon) { onClose(); navigate('/login'); return; }

    setErr(null);
    setPending(true);
    try {
      const { url } = await billing.checkoutSession(isLife ? 'lifetime' : yearly ? 'annual' : 'monthly');
      window.location.href = url;
    } catch (e) {
      setPending(false);
      const api = e as { status?: number; code?: string; detail?: string };
      // Session expired mid-flow → sign in again rather than showing a dead end.
      if (api?.status === 401) { onClose(); navigate('/login'); return; }
      if (api?.code === 'email_unverified') {
        setErr(api.detail || 'Confirm your email address first — check your inbox for the link.');
        return;
      }
      setErr('Could not start checkout — please try again.');
    }
  }

  // ── The head: the only part that differs between ceilings ──────────────────
  // Each block owns its eyebrow (what just happened), headline (what Pro does
  // about it), one sentence, and the picture. The rest of the card is shared.
  let eyebrow: React.ReactNode;
  let title: string;
  let sub: React.ReactNode;
  let hero: React.ReactNode = null;

  // The track sketch, reused by both ceilings that cut a song short — it is
  // literally the same picture in both cases.
  // With a freeWindow the lit bars are the ones whose centre falls inside it
  // (never fewer than one); without, it is the original first-N sketch.
  const windowLit = (i: number) => {
    if (!freeWindow || !(freeWindow.total > 0)) return i < BARS_FREE;
    const n = BAR_HEIGHTS.length;
    const a = freeWindow.start / freeWindow.total;
    const b = freeWindow.end / freeWindow.total;
    const c = (i + 0.5) / n;
    return (c >= a && c <= b) || i === Math.min(n - 1, Math.floor(a * n));
  };
  const clock = (sec: number) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
  const waveHero = (cap: number) => (
    <div className="rlfs-wave">
      <div className="rlfs-bars" aria-hidden="true">
        {bars.map((b, i) => (
          <span key={i} className={(freeWindow ? windowLit(i) : b.free) ? 'rlfs-bar is-free' : 'rlfs-bar'} style={{ height: b.h }} />
        ))}
      </div>
      <div className="rlfs-wave-legend">
        <span className="rlfs-wave-free">{freeWindow ? `${clock(freeWindow.start)} – ${clock(freeWindow.end)}` : <>0:00 – {capStamp(cap)}</>}</span>
        <span className="rlfs-wave-freetag">free</span>
        <span className="rlfs-wave-rule" />
        <span className="rlfs-wave-locked"><LockIcon size={11} /> the rest of the track</span>
      </div>
    </div>
  );

  // The chord row as the user sees it behind this dialog: the bars free has
  // named, then the rest of the track with its names withheld. Same legend as
  // the waveform sketch, because it is the same idea — a ceiling on a timeline.
  const chordHero = (cap: number) => (
    <div className="rlfs-wave">
      <div className="rlfs-chords" aria-hidden="true">
        {CHORD_RIBBON.map((c, i) => (
          <span key={i} className={i < CHORDS_FREE ? 'rlfs-chord is-free' : 'rlfs-chord'}>{c}</span>
        ))}
      </div>
      <div className="rlfs-wave-legend">
        <span className="rlfs-wave-free">0:00 – {capStamp(cap)}</span>
        <span className="rlfs-wave-freetag">free</span>
        <span className="rlfs-wave-rule" />
        <span className="rlfs-wave-locked"><LockIcon size={11} /> the rest of the track</span>
      </div>
    </div>
  );

  if (variant === 'quality') {
    // The object behind this dialog is the Quality switch the user just
    // flipped, so the picture is that choice: the setting they have and the
    // one Pro adds — the WAV ceiling's two-chip shape, no new furniture.
    // Describes the OUTCOME only; naming an engine in user-facing copy is a
    // release blocker.
    eyebrow = <><SparkIcon size={17} /><span>Best quality is Pro</span></>;
    title = 'Pull the parts further apart';
    sub = (
      <>
        Best quality separates with less bleed between instruments — it tells
        most on dense mixes and quiet passages. Standard stays free and handles
        most tracks well.
      </>
    );
    hero = (
      <div className="rlfs-wave">
        <div className="rlfs-fmts">
          <span className="rlfs-fmt is-free"><b>Standard</b> free</span>
          <span className="rlfs-fmt is-locked"><LockIcon size={11} /><b>Best</b> cleaner parts</span>
        </div>
        <div className="rlfs-wave-legend">
          <span className="rlfs-wave-freetag">Same parts, less bleed between them.</span>
        </div>
      </div>
    );
  } else if (variant === 'chords') {
    eyebrow = <><LockIcon size={17} /><span>{freeChordsSeconds}-second chord limit</span></>;
    title = 'See the chords for the whole song';
    sub = (
      <>
        Free reads the chords for the first {capAfterFirst(freeChordsSeconds)}. Pro maps
        the whole track — every chord on the timeline, and the shapes to play them.
      </>
    );
    hero = chordHero(freeChordsSeconds);
  } else if (variant === 'quota') {
    // ⚠️ NO HERO, and NO REFILL COUNTDOWN — both deliberate.
    //
    // A picture only earns its place when it shows something the words cannot.
    // The 'length' ceiling has one: the track with its free minute lit is the
    // same thing the user is staring at behind the modal. This ceiling has no
    // such object — it is a count and a clock — and the first attempt (a row of
    // spent pips) both READ AS A SKELETON LOADER and restated the sentence
    // directly above it. Redundant furniture that says "still loading" is worse
    // than empty space, so the head is copy only and the offer arrives sooner.
    //
    // The countdown is an owner decision (2026-08-15, see UpgradeModal.tsx): the
    // out-of-separations card never says WHEN the batch refills, because
    // quantifying the wait invites waiting rather than deciding. The 'remaining'
    // heads-up is the variant that may name a time — it is a warning, not a wall.
    eyebrow = <><ClockIcon size={17} /><span>No separations left</span></>;
    title = 'Keep going without the wait';
    sub = isAnon ? (
      <>
        You&rsquo;ve used all {dailyQuota} free separations. Sign in for your own
        allowance, or go Pro and never wait for a refill.
      </>
    ) : (
      <>
        Free covers {dailyQuota} separations every 24&nbsp;hours. Pro lifts the
        limit — separate as many songs as you want, whenever you want.
      </>
    );
    hero = null;
  } else if (variant === 'export') {
    if (isWav) {
      eyebrow = <><LockIcon size={17} /><span>WAV is a Pro format</span></>;
      title = 'Download in lossless quality';
      sub = (
        <>
          WAV keeps every detail of the separated parts — uncompressed and full
          length, the format to bring into a DAW. MP3 stays free.
        </>
      );
      hero = (
        <div className="rlfs-wave">
          <div className="rlfs-fmts">
            <span className="rlfs-fmt is-free"><b>MP3</b> free</span>
            <span className="rlfs-fmt is-locked"><LockIcon size={11} /><b>WAV</b> lossless</span>
          </div>
          <div className="rlfs-wave-legend">
            <span className="rlfs-wave-freetag">Both carry every channel and the full mix.</span>
          </div>
        </div>
      );
    } else {
      eyebrow = <><LockIcon size={17} /><span>{exportSeconds}-second export limit</span></>;
      title = 'Export the whole section';
      sub = (
        <>
          A free export covers the first {capAfterFirst(exportSeconds)}. Pro writes
          the section out at full length, and unlocks lossless WAV.
        </>
      );
      hero = waveHero(exportSeconds);
    }
  } else if (freeWindow) {
    // The auto-split page: the free minute was chosen for them and has just
    // been heard (edge) or is being looked at (header, badge), so the head
    // names the song they are in rather than restating the plan's number.
    eyebrow = <><LockIcon size={17} /><span>{trigger === 'edge' ? 'Your free minute is up' : 'Full songs are Pro'}</span></>;
    title = 'Split the full song';
    sub = freeWindow.busiest ? (
      <>
        Free split this song&rsquo;s busiest minute. Pro takes the whole track — every
        verse, every solo — and maps the chords all the way to the end.
      </>
    ) : (
      <>
        Free separates one minute of a song. Pro takes the whole track — every verse,
        every solo — and maps the chords all the way to the end.
      </>
    );
    hero = waveHero(freeSeconds);
  } else {
    eyebrow = <><LockIcon size={17} /><span>{freeSeconds}-second limit reached</span></>;
    title = 'Unlock the full song';
    sub = (
      <>
        Free separates the first {capAfterFirst(freeSeconds)}. Pro takes the whole track — every
        verse, every solo — and maps the chords all the way to the end.
      </>
    );
    hero = waveHero(freeSeconds);
  }

  // Three benefits per ceiling: the one that answers the limit they hit first,
  // then the two that widen the offer beyond it.
  // ⚠️ The lyrics sheet is deliberately NOT listed: it is FREE on both surfaces,
  // and a paid card must never advertise something the user already has. The
  // beat-locked click IS a real Studio Pro benefit (metro.limitSong =
  // FREE_METRONOME_SECONDS when free) — but it is NOT gated in the extension,
  // so the extension's card must not claim it. Verified at the gate sites.
  // The quota card is the only one whose pitch is BREADTH — no single ceiling is
  // the story, so it shows the whole product (8 tiles, 4 across). Every other
  // variant answers the one wall the user just hit and stays at 3. Matches the
  // extension's daily-limit card, which was widened the same way on 2026-09-04.
  const benefits =
    variant === 'quota'
      ? [
          { icon: B_INFINITY, b: 'No daily limit', s: 'Separate as many songs as you want, any time.' },
          { icon: <>{B_SONG}{B_SONG2}</>, b: 'The whole song', s: 'Full tracks, not a one-minute slice.' },
          { icon: B_CHORDS, b: 'Chords, key & shapes', s: 'Every chord to the last bar, and how to play it.' },
          { icon: B_METRONOME, b: 'A click that follows', s: "A metronome locked to the song's own beat." },
          { icon: B_DOWNLOAD, b: 'Export your stems', s: 'Full-length parts from your own uploads.' },
          { icon: B_SPARK, b: 'Best quality', s: 'Cleaner parts, with less bleed between them.' },
          { icon: B_LAYERS, b: 'Unlimited saved loops', s: 'Free keeps 3 per song — Pro keeps every one.' },
          { icon: B_SYNC, b: 'Everywhere you practice', s: 'Saved loops sync with the extension.' },
        ]
      : variant === 'quality'
      ? [
          { icon: B_SPARK, b: 'Best quality', s: 'Cleaner parts, with less bleed between them.' },
          { icon: <>{B_SONG}{B_SONG2}</>, b: 'The whole song', s: 'Full tracks, not a one-minute slice.' },
          { icon: B_CHORDS, b: 'Every chord', s: 'Chords to the last bar, with the shapes to play them.' },
        ]
      : variant === 'chords'
      ? [
          { icon: B_CHORDS, b: 'Every chord', s: 'Chords to the last bar, with the shapes to play them.' },
          { icon: <>{B_SONG}{B_SONG2}</>, b: 'The whole song', s: 'Separate a full track, not a one-minute slice.' },
          { icon: B_METRONOME, b: 'A click that follows', s: "A metronome locked to the song's own beat." },
        ]
      : variant === 'export'
      ? [
          { icon: B_DOWNLOAD, b: 'Full-length files', s: 'The whole section, not a preview of it.' },
          { icon: B_WAVE, b: 'Lossless WAV', s: 'Uncompressed audio, ready for a DAW.' },
          { icon: B_LAYERS, b: 'Every channel', s: 'Each part on its own, or the full mix.' },
        ]
      : [
          { icon: <>{B_SONG}{B_SONG2}</>, b: 'The whole song', s: 'Separate a full track, not a one-minute slice.' },
          { icon: B_CHORDS, b: 'Every chord', s: 'Chords to the last bar, with the shapes to play them.' },
          { icon: B_DOWNLOAD, b: 'Keep the stems', s: 'Export full-length parts from your own uploads.' },
        ];

  return (
    // rlfs-backdrop only adds scrolling to the shared backdrop, scoped to this
    // card — the global .rl-modal-backdrop centers without overflow, which would
    // clip a card taller than a phone viewport at both ends.
    <div className="rl-modal-backdrop rlfs-backdrop" role="dialog" aria-modal="true" aria-labelledby="rlfs-title" onClick={onClose}>
      <div className="rlfs" onClick={(e) => e.stopPropagation()}>
        <button className="rlfs-x" onClick={onClose} aria-label="Close">×</button>

        <div className="rlfs-head">
          <div className="rlfs-eyebrow">{eyebrow}</div>
          <h2 id="rlfs-title">{title}</h2>
          <p className="rlfs-sub">{sub}</p>
          {hero}
        </div>

        <div className={benefits.length > 3 ? 'rlfs-benefits is-wide' : 'rlfs-benefits'}>
          {benefits.map((x) => (
            <div className="rlfs-benefit" key={x.b}>
              <BenefitIcon>{x.icon}</BenefitIcon>
              <b>{x.b}</b>
              <span>{x.s}</span>
            </div>
          ))}
        </div>

        {/* Always rendered — hiding it when Lifetime is picked made the whole
            card reflow on a click. The cycle only means anything for Pro, so
            choosing one selects Pro rather than sitting inert. */}
        <div className={isLife ? 'rlfs-cycle is-idle' : 'rlfs-cycle'}>
          <div className="rlfs-seg">
            <button
              className={!isLife && !yearly ? 'rlfs-seg-b on' : 'rlfs-seg-b'}
              onClick={() => { setCycle('monthly'); setPlan('pro'); }}
            >Monthly</button>
            <button
              className={!isLife && yearly ? 'rlfs-seg-b on' : 'rlfs-seg-b'}
              onClick={() => { setCycle('yearly'); setPlan('pro'); }}
            >Yearly</button>
          </div>
          <span className="rlfs-save">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
            Save 45% yearly
          </span>
        </div>

        <div className="rlfs-plans">
          <button
            className={isLife ? 'rlfs-plan' : 'rlfs-plan is-on'}
            onClick={() => setPlan('pro')}
            aria-pressed={!isLife}
          >
            <span className="rlfs-tag rlfs-tag-pro">Most popular</span>
            <span className="rlfs-plan-top">
              <span className={isLife ? 'rlfs-radio' : 'rlfs-radio is-on'} />
              <span className="rlfs-plan-name">Pro</span>
            </span>
            <span className="rlfs-price">
              <b>{yearly ? '$3.25' : '$5.95'}</b><i>/month</i>
            </span>
            <span className="rlfs-plan-note">
              {yearly ? 'Billed $39/year · cancel anytime' : 'Billed monthly · cancel anytime'}
            </span>
          </button>

          <button
            className={isLife ? 'rlfs-plan is-gold' : 'rlfs-plan'}
            onClick={() => setPlan('lifetime')}
            aria-pressed={isLife}
          >
            <span className="rlfs-tag rlfs-tag-life">Best value</span>
            <span className="rlfs-plan-top">
              <span className={isLife ? 'rlfs-radio is-gold' : 'rlfs-radio'} />
              <span className="rlfs-plan-name">Founder's Lifetime</span>
            </span>
            <span className="rlfs-price">
              <b>$99</b><i>once</i>
            </span>
            <span className="rlfs-plan-note">Pay once, yours forever — launch offer.</span>
          </button>
        </div>

        <div className="rlfs-foot">
          {err && <div className="rlfs-err">{err}</div>}
          <button
            className={isLife ? 'rlfs-cta is-gold' : 'rlfs-cta'}
            onClick={onBuy}
            disabled={pending}
          >
            {pending ? 'Opening checkout…' : isAnon ? 'Sign in to go Pro' : ctaLabel}
          </button>
          <div className="rlfs-foot-row">
            <span className="rlfs-foot-note">
              {isAnon
                ? 'Sign in first so your plan follows your account'
                : isLife ? 'Every future Pro feature included' : 'Unlocks the moment you pay · cancel anytime'}
            </span>
            {/* The way through, when the ceiling has one. Keeping it here means
                the export the user asked for still completes from inside the
                offer, so this card never becomes a dead end. */}
            <button className="rlfs-ghost" onClick={secondary ? secondary.onClick : onClose}>
              {secondary ? secondary.label : 'Keep using free'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
